import { formatDate, isDate } from 'date-fns'
import { CircleAlert, TriangleAlert } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { Button } from '@fortem-monorepo/react-ui'
import { BatchExecutor } from '@fortem-monorepo/web-sdk'

const safeFormatDate = (...args: Parameters<typeof formatDate>) => {
  const date = args[0]
  try {
    return formatDate(...args)
  } catch {
    return isDate(date) ? toISO8601(date) : date
  }
}

export interface ReportDateButtonProps extends React.ComponentProps<typeof Button> {
  date: string
  active?: boolean
  reports: Array<BatchExecutor.ReportItem>
  reportIsAnomaly: (report: BatchExecutor.ReportItem) => boolean
}

export const ReportDateButton: React.FC<ReportDateButtonProps> = ({
  date,
  active = false,
  reports,
  reportIsAnomaly,
  ...props
}) => {
  const inProgressPercent =
    (reports.filter((assessment: any) => !assessment.completiontimestamp && !assessment.crashed)
      .length /
      reports.length) *
    100

  const completedPercent =
    (reports.filter((assessment: any) => assessment.completiontimestamp && !assessment.crashed)
      .length /
      reports.length) *
    100

  const crashedPercent =
    (reports.filter((assessment: any) => !assessment.completiontimestamp && assessment.crashed)
      .length /
      reports.length) *
    100

  const hasAnomalies =
    reports.filter((report) => {
      return report.time_taken_minutes && reportIsAnomaly(report)
    }).length > 0

  return (
    <Button
      className={cn('py-8 px-7 cursor-pointer relative', {
        ['bg-accent']: active,
      })}
      variant={'outline'}
      size="lg"
      {...props}
    >
      <span className={'flex flex-col'}>
        {!!crashedPercent ? (
          <span className={'absolute top-2 right-3 text-destructive'}>
            <TriangleAlert />
          </span>
        ) : (
          hasAnomalies && (
            <span className={'absolute top-2 right-3 text-amber-600'}>
              <CircleAlert />
            </span>
          )
        )}
        <span className={'text-xs text-muted-foreground uppercase'}>
          {safeFormatDate(date, 'MMM')}
        </span>
        <span className={'whitespace-nowrap'}>{safeFormatDate(date, 'EEE d')}</span>
        <span
          className={
            'h-1 w-full min-w-[50px] rounded-full bg-muted mt-2 flex items-center overflow-hidden'
          }
        >
          {!!inProgressPercent && (
            <span
              className={'bg-amber-300 h-1'}
              style={{
                width: `${inProgressPercent}%`,
              }}
            />
          )}
          {!!completedPercent && (
            <span
              className={'bg-success h-1'}
              style={{
                width: `${completedPercent}%`,
              }}
            />
          )}
          {!!crashedPercent && (
            <span
              className={'bg-destructive h-1'}
              style={{
                width: `${crashedPercent}%`,
              }}
            />
          )}
        </span>
      </span>
    </Button>
  )
}
